/**
  Intro:

  This routes file contains the appropriate format for the following module(s)

  Module:
    => react-router-config
        - AssociatedMethod(s):
            => renderRoutes(routes)
            => matchRoutes(routes, pathname)

  Please provide the name representation of the route, as a way to `title-lize`
  matched routes within the shared react components

  Usage:  ----------------------------------------------------------------------

    import { routes } from 'scripts/routes.js'

    class MyReactApp extend component {
      .
      .
      .

      render() {
        return (
          <Router-Unit-Here>
            { renderRoutes(routes) }
          </Router-Unit-Here>
        )
      }
    }

  ------------------------------------------------------------------------------
**/

// import {Home, Article, Listings, NotFound, WordpressPages, Search} from './sync-bundle'
import { Home, Article, Listings, WordpressPages, NotFound, Search, ProfileEdit, SubmitTip } from './sync-bundle'
import Constants from 'scripts/base/Constants'
import { dateDiff } from 'scripts/lib/utils'
import { soompi } from 'scripts/api/SoompiApi'
import RequireAuth from 'scripts/components/base/RequireAuth'

const { Routes } = Constants

export const routes = [
  {
    component: Home,
    path: Routes.HOME,
    exact: true,
    sensitive: true,
    loadData: function(param, match, lang) {
      return new Promise((resolve, reject) => {
        soompi.setLanguage(lang)
        soompi.getLatest({ perPage: 13, page: 1 }).promise.then((result) => {
          soompi.setLanguage(lang)
          return soompi.getTrending({perPage: 4, page: 1}).promise.then((trending) => {
            soompi.setLanguage(lang)
            return soompi.getTopic(Constants.SponsoredItem.type)({ id: Constants.SponsoredItem.id }, { page: 1, perPage: 1 }).promise.then((topic) => {
              let article = topic.results[0]
              if (article) {
                const date = new Date(article.createdAt * 1000)
                if (Constants.SponsoredItem.maxAge && dateDiff(date) > Constants.SponsoredItem.maxAge) {
                  article = false
                } else {
                  // check for duplicates
                  trending.results = trending.results.filter(item => item.id !== article.id)
                  trending.results.unshift(article)
                }
              }
              resolve({ feed: result.results, trending: trending })
            }).catch(e => {
              resolve({ feed: result.results, trending: trending })
            })
          }).catch((err) => {
            return reject(err)
          })
        }).catch((err) => {
          return reject(err)
        })
      })
    },
    name: 'home'
  },
  {
    component: Article,
    exact: true,
    sensitive: true,
    path: Routes.ARTICLE,
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getArticle({ id: param }).promise
    },
    name: 'article'
  },
  {
    component: Listings,
    path: Routes.LISTINGS,
    exact: true,
    sensitive: true,
    name: 'all_articles',
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getPostsBy(param)({ perPage: 15, page: 1 }).promise.then((response) => {
        return {posts: response}
      }).catch(e => {
        throw new Error('not found')
      })
    }
  },
  {
    component: Listings,
    path: Routes.CATEGORIES,
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getCategoryInfo({ slug: param }).promise.then((response) => {
        if (response.results && response.results.length > 0) {
          const id = response.results[0].id
          const category = response.results[0]
          let sort = match.params['sort'] || null
          sort = sort === 'popular' ? 'popularity' : sort
          soompi.setLanguage(lang)
          return soompi.getTopic('category')({id: id}, { perPage: 15, page: 1, sort: sort }).promise.then((response) => {
            return {topic: category, posts: response}
          })
        } else {
          throw new Error('not found')
        }
      })
    },
    exact: true,
    sensitive: true,
    name: 'category'
  },
  {
    component: Listings,
    path: Routes.TAGS,
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getTagInfo({ slug: param }).promise.then((response) => {
        if (response.results && response.results.length > 0) {
          const id = response.results[0].id
          const tag = response.results[0]
          let sort = match.params['sort'] || null
          sort = sort === 'popular' ? 'popularity' : sort
          soompi.setLanguage(lang)
          return soompi.getTopic('tag')({id: id}, { perPage: 15, page: 1, sort: sort }).promise.then((response) => {
            return {topic: tag, posts: response}
          })
        } else {
          soompi.setLanguage(lang)
          return soompi.getFCInfo({ slug: param }).promise.then((response) => {
            if (response.results && response.results.length > 0) {
              return {redirect: {path: `/fanclub/${param}`, status: 301}}
            } else {
              throw new Error('not found')
            }
          })
        }
      })
    },
    exact: true,
    sensitive: true,
    name: 'tag'
  },
  {
    component: Listings,
    path: Routes.FANCLUBS,
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getFCInfo({ slug: param }).promise.then((response) => {
        if (response.results && response.results.length > 0) {
          const id = response.results[0].id
          const fanClub = response.results[0]
          let sort = match.params['sort'] || null
          sort = sort === 'popular' ? 'popularity' : sort
          soompi.setLanguage(lang)
          return soompi.getTopic('fanClub')({id: id}, { perPage: 15, page: 1, sort: sort }).promise.then((response) => {
            return {topic: fanClub, posts: response}
          })
        } else {
          soompi.setLanguage(lang)
          return soompi.getTagInfo({ slug: param }).promise.then((response) => {
            if (response.results && response.results.length > 0) {
              return {redirect: {path: `/tag/${param}`, status: 301}}
            } else {
              throw new Error('not found')
            }
          })
        }
      })
    },
    exact: true,
    sensitive: true,
    name: 'fanClub'
  },
  {
    component: Listings,
    path: Routes.AUTHORS,
    loadData: function(param, match, lang) {
      soompi.setLanguage(lang)
      return soompi.getAuthorInfo({id: param}).promise.then((response) => {
        if (response) {
          const id = response.id
          const author = response
          let sort = match.params['sort'] || null
          sort = sort === 'popular' ? 'popularity' : sort
          soompi.setLanguage(lang)
          return soompi.getAuthorPosts({ id }, { perPage: 15, page: 1, sort }).promise.then((response) => {
            return {author, posts: response}
          })
        }
      })
    },
    exact: true,
    sensitive: true,
    name: 'author'
  },
  {
    component: RequireAuth(ProfileEdit),
    path: Routes.PROFILE_EDIT,
    exact: true,
    sensitive: true,
    name: 'settings',
    loadData: function() {
      return new Promise(resolve => {
        resolve({
          title: {rendered: 'Edit Profile'},
          content: {rendered: ''},
          date: null,
          excerpt: {rendered: ''}
        })
      })
    }
  },
  {
    component: Search,
    path: Routes.SEARCH,
    exact: true,
    sensitive: true,
    name: 'search',
    loadData: function() {
      return new Promise(resolve => {
        resolve({
          title: {rendered: 'Search'},
          content: {rendered: ''},
          date: null,
          excerpt: {rendered: ''}
        })
      })
    }
  },
  {
    component: SubmitTip,
    path: Routes.SUBMIT_TIP,
    exact: true,
    sensitive: true,
    name: 'submit_tip',
    loadData: function() {
      return new Promise(resolve => {
        resolve({
          title: {rendered: 'Submit Tip'},
          content: {rendered: ''},
          date: null,
          excerpt: {rendered: ''}
        })
      })
    }
  },
  {
    component: WordpressPages,
    path: Routes.PAGES,
    exact: true,
    sensitive: true,
    name: 'wp_page',
    loadData: function(param, match, lang) {
      const pages = Constants.WordpressPages[lang]
      const pageId = pages[param.toLowerCase()] || Constants.WordpressPages[Constants.DefaultLang][param.toLowerCase()]
      const apiPrefix = pages[param.toLowerCase()] && lang !== Constants.DefaultLang ? `${lang}/` : ''
      return soompi.request(`${apiPrefix}wp-json/wp/v2/pages/${pageId}`, { method: 'get' }, { apiEndpoint: 'https://www.soompi.com' }).then((result) => {
        return result.data
      })
    }
  },
  {
    component: WordpressPages,
    path: Routes.JOB_PAGES,
    exact: true,
    sensitive: true,
    name: 'wp_page',
    loadData: function(param) {
      return soompi.request(`wp-json/wp/v2/pages/${Constants.WordpressJobPages[param.toLowerCase()]}`, { method: 'get' }, { apiEndpoint: 'https://www.soompi.com' }).then((result) => {
        return result.data
      })
    }
  },
  {
    isNotFoundPath: true,
    component: NotFound,
    path: Routes.ALL,
    name: 'error_404'
  }
]



// WEBPACK FOOTER //
// ./assets/js/routes.js